import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { Configuration } from "@models/configuration.model";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { AccountService } from "@services/accounts.service";
import { FundService } from "@services/funds.service";
import { GfhTransactionService } from "@services/gfh-transaction.service";
import { catchError, EMPTY, mergeMap, Observable, of, switchMap } from "rxjs";

import {
  ACCOUNTS_ACTION_FAILURE,
  LOAD_ACCOUNT_CONFIGURATION,
  LOAD_ACCOUNTS_FOR_CLIENT,
  LOAD_DISBURSEMENTS_FOR_ACCOUNT,
  LOAD_FUNDS,
  LOAD_GFH_TRANSACTIONS_FOR_ACCOUNT,
  SET_ACCOUNT_CONFIGURATION,
  SET_ACCOUNTS_FOR_CLIENT,
  SET_ACCOUNTS_LOADING,
  SET_DISBURSEMENTS_FOR_ACCOUNT,
  SET_FUNDS,
  SET_GFH_TRANSACTIONS_FOR_ACCOUNT,
} from "./accounts.actions";

@Injectable()
export class AccountsEffects {
  loadAccounts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LOAD_ACCOUNTS_FOR_CLIENT),
      mergeMap(({ id }): Observable<Action> => {
        this.store.dispatch(SET_ACCOUNTS_LOADING({ loading: true }));

        return this.accountService.getAccountsForClient$(id).pipe(
          switchMap((data) => {
            this.store.dispatch(SET_ACCOUNTS_LOADING({ loading: false }));

            return of(SET_ACCOUNTS_FOR_CLIENT({ data }));
          }),
          catchError((error: HttpErrorResponse) => {
            this.store.dispatch(SET_ACCOUNTS_LOADING({ loading: false }));
            return of(ACCOUNTS_ACTION_FAILURE({ error }));
          })
        );
      })
    );
  });

  loadGroupedGfhTransactions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LOAD_GFH_TRANSACTIONS_FOR_ACCOUNT),
      mergeMap(({ id }): Observable<Action> => {
        return this.ghfTransactionService.getGroupedGfhTransactions$(id).pipe(
          switchMap((data) => {
            return of(
              SET_GFH_TRANSACTIONS_FOR_ACCOUNT({
                groupedGfhTransactions: data.groupedGfhTransactions,
              })
            );
          }),
          catchError(() => {
            return of(
              SET_GFH_TRANSACTIONS_FOR_ACCOUNT({ groupedGfhTransactions: [] })
            );
          })
        );
      })
    );
  });

  loadFunds$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LOAD_FUNDS),
      mergeMap((): Observable<Action> => {
        return this.fundService.getFundsIncludingLatestNavValue$().pipe(
          switchMap((funds) => {
            return of(SET_FUNDS({ funds }));
          }),
          catchError(() => {
            return of(SET_FUNDS({ funds: [] }));
          })
        );
      })
    );
  });

  loadAccountConfiguration$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(LOAD_ACCOUNT_CONFIGURATION),
      mergeMap((): Observable<Action> => {
        return this.accountService.getAccountConfiguration$().pipe(
          switchMap((configuration: Configuration): Observable<Action> => {
            return of(
              SET_ACCOUNT_CONFIGURATION({
                configuration,
              })
            );
          }),
          catchError((): Observable<Action> => {
            return EMPTY;
          })
        );
      })
    );
  });

  loadDisbursements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LOAD_DISBURSEMENTS_FOR_ACCOUNT),
      mergeMap((action: { gfhAccountId: string }): Observable<Action> => {
        return this.accountService.getDisbursements$(action.gfhAccountId).pipe(
          switchMap((disbursements) => {
            return of(SET_DISBURSEMENTS_FOR_ACCOUNT({ disbursements }));
          }),
          catchError(() => {
            return of(SET_DISBURSEMENTS_FOR_ACCOUNT({ disbursements: [] }));
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private accountService: AccountService,
    private ghfTransactionService: GfhTransactionService,
    private fundService: FundService,
    private store: Store
  ) {}
}
